import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	getGlobalDocxFlowDocument,
	saveGlobalDocxFlowDocument,
} from '@dealstack/frontend/shared/dataExchange/services/documentTemplates.service';
import {
	useTranslationDefault,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import {
	getApiErrorMessage,
	isStandardBackendError,
} from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';

export const buildGlobalDocxFlowDocumentQueryKey = (templateId: string): string =>
	`global-docx-flow-document-${templateId}`;

export const useGlobalDocxFlowDocumentQuery = (templateId: string | null) =>
	useQuery({
		queryKey: [buildGlobalDocxFlowDocumentQueryKey(templateId ?? '')],
		queryFn: () => getGlobalDocxFlowDocument(templateId ?? ''),
		enabled: !!templateId,
		// The editor holds the working copy in ProseMirror; refetching under it would discard
		// unsaved edits, so this is fetched once per open and only invalidated on save.
		staleTime: Infinity,
		refetchOnWindowFocus: false,
	});

export const useSaveGlobalDocxFlowDocument = () => {
	const tMipManage = useTranslationMipManage();
	const tDefault = useTranslationDefault();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (args: {
			templateId: string;
			document: unknown;
			bindings?: Record<string, string>;
		}) =>
			await saveGlobalDocxFlowDocument({
				id: args.templateId,
				document: args.document,
				...(args.bindings === undefined ? {} : { bindings: args.bindings }),
			}),
		onSuccess: async (_result, { templateId }) => {
			await queryClient.invalidateQueries({
				queryKey: [buildGlobalDocxFlowDocumentQueryKey(templateId)],
			});
			showToastV2({ title: tMipManage('master_documents.toast.updated') });
		},
		onError: (error: unknown) => {
			if (isStandardBackendError(error)) {
				showToastV2({
					state: 'error',
					title: tDefault(`error_codes.${error.error?.code}`),
				});
				return;
			}
			// The server's own sentence. `saveDocument`'s refusals are plain BadRequestExceptions,
			// so none carries a `code`; the messages are authored for this toast and carry no ids.
			const reason = getApiErrorMessage(error);
			showToastV2({
				state: 'error',
				title: tDefault('common.toast.error.message'),
				...(reason === undefined ? {} : { message: reason }),
			});
		},
	});
};
